import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import { todayString } from '../utils/appDateTime';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import DateInput from './ui/date-input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { CheckCircle, XCircle, Clock, AlertCircle, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', icon: CheckCircle },
  { value: 'absent', label: 'Absent', icon: XCircle },
  { value: 'half_day', label: 'Half-Day', icon: Clock },
  { value: 'leave', label: 'Leave', icon: AlertCircle },
];

function StatusButtons({ teacherId, attendance, isFutureDate, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {STATUS_OPTIONS.map((opt) => (
        <Button
          key={opt.value}
          type="button"
          variant={attendance[teacherId] === opt.value ? 'default' : 'outline'}
          size="sm"
          disabled={isFutureDate}
          title={opt.label}
          onClick={() => onChange(teacherId, opt.value)}
          className={
            attendance[teacherId] === opt.value
              ? opt.value === 'present'
                ? 'bg-green-600'
                : opt.value === 'absent'
                  ? 'bg-red-600'
                  : opt.value === 'half_day'
                    ? 'bg-amber-600'
                    : 'bg-blue-600'
              : 'bg-muted text-muted-foreground'
          }
        >
          <opt.icon className="w-4 h-4" />
        </Button>
      ))}
    </div>
  );
}

function PaginationBar({ meta, page, setPage }) {
  if (meta.pages <= 1) return null;
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4">
      <p className="text-sm text-muted-foreground">Page {meta.page} of {meta.pages} ({meta.total} teachers)</p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
        <Button variant="outline" size="sm" disabled={page >= meta.pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>
    </div>
  );
}

export default function TeacherAttendance() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState(todayString());
  const [teachers, setTeachers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ page: 1, limit: 50, pages: 1, total: 0 });
  const [page, setPage] = useState(1);

  const today = todayString();
  const isFutureDate = selectedDate > today;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date: selectedDate,
        page,
        limit: 50,
        ...(user?.school_id ? { school_id: user.school_id } : {}),
      };
      const res = await api.get('/teachers/attendance', { params });
      const payload = res.data?.data ?? {};
      const list = payload.teachers ?? [];
      setTeachers(list);
      setMeta(payload.meta ?? { page: 1, limit: 50, pages: 1, total: list.length });
      const map = {};
      list.forEach((t) => {
        map[t.teacher_id] = t.status || 'absent';
      });
      setAttendance(map);
    } catch (e) {
      setTeachers([]);
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load teachers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, page, user?.school_id, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = (teacherId, status) => {
    setAttendance((prev) => ({ ...prev, [teacherId]: status }));
  };

  const handleSave = async () => {
    if (isFutureDate) {
      toast({ title: 'Future date', description: 'Cannot mark attendance for future dates', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const records = Object.entries(attendance).map(([teacher_id, status]) => ({
        teacher_id: Number(teacher_id),
        status,
      }));
      await api.post('/teachers/attendance/mark', {
        date: selectedDate,
        school_id: user?.school_id,
        records,
      });
      toast({ title: 'Saved', description: 'Teacher attendance recorded.' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Could not save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold">Teacher Attendance</h1>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center w-full sm:w-auto">
          <DateInput
            value={selectedDate}
            max={today}
            onChange={(v) => { setSelectedDate(v); setPage(1); }}
            className="w-full sm:w-auto"
          />
          <Button onClick={handleSave} disabled={saving || isFutureDate || loading} className="w-full sm:w-auto">
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daily attendance</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-muted-foreground">Loading…</p> : null}
          {!loading && teachers.length === 0 ? (
            <p className="text-muted-foreground">No teachers found.</p>
          ) : null}

          {/* Mobile: stacked cards */}
          {!loading && teachers.length > 0 ? (
            <div className="space-y-3 sm:hidden">
              {teachers.map((t) => (
                <div key={t.teacher_id} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{t.name}</p>
                    <p className="text-sm text-muted-foreground">{t.employee_id || '—'}</p>
                  </div>
                  <StatusButtons
                    teacherId={t.teacher_id}
                    attendance={attendance}
                    isFutureDate={isFutureDate}
                    onChange={handleStatusChange}
                  />
                </div>
              ))}
            </div>
          ) : null}

          {/* Desktop: table */}
          {!loading && teachers.length > 0 ? (
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Teacher</TableHead>
                    <TableHead>Employee ID</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teachers.map((t) => (
                    <TableRow key={t.teacher_id}>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell>{t.employee_id || '—'}</TableCell>
                      <TableCell>
                        <StatusButtons
                          teacherId={t.teacher_id}
                          attendance={attendance}
                          isFutureDate={isFutureDate}
                          onChange={handleStatusChange}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          <PaginationBar meta={meta} page={page} setPage={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}
